import CustomSourceflowText from "@/components/Shared/CustomSourceflowText";
import CustomSwiper from "@/components/Swiper/CustomSwiper";
import CustomSwiperSlide from "@/components/Swiper/CustomSwiperSlide";
import Modal from "@/components/Shared/Modal";
import ModalTeamMember from "@/components/Team/TeamMemberModal";
import PrimaryBtn from "@/components/Buttons/PrimaryButton";
import useLocale from "@/hooks/useLocale";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@react-hook/media-query";

import TeamMembersCard from "./components/TeamMembersCard";
import ActiveLocationCta from "./components/ActiveLocationCta";

export default function TeamMembers({
  color,
  hoverColor,
  location,
  data,
  contact,
}) {
  const locale = useLocale();
  const md = useMediaQuery("(max-width: 992px)");
  const [isMobile, setMobile] = useState(false);
  const [activeLocation, setActiveLocation] = useState(null);
  const [selectedLocationDetail, setSelectedLocationDetail] = useState();
  const [open, setOpen] = useState(false);
  const [activeContent, setActiveContent] = useState("");

  const activeMembers = useMemo(() => {
    if (activeLocation === null) {
      return data.map(
        ({
          id,
          name,
          email,
          phone,
          role,
          linkedin_link,
          specialisation,
          favourite_food,
          regions_operate_on,
          top_hobby,
          favourite_tv_show_movie,
          favourite_music,
          location,
          tags,
          profile_picture,
          other_pictures,
        }) => ({
          id: id,
          title: name,
          email: email,
          role: role,
          telephone: phone,
          linkedIn: linkedin_link,
          specialism: specialisation,
          food: favourite_food,
          region: regions_operate_on,
          hobby: top_hobby,
          movie: favourite_tv_show_movie,
          music: favourite_music,
          location: location.join(", "),
          tag: tags,
          profile_picture: profile_picture,
          other_pictures: other_pictures,
        }),
      );
    } else {
      const locationMembers = data.filter(
        (item) => item.tags === activeLocation,
      );
      return locationMembers.map(
        ({
          id,
          name,
          email,
          role,
          phone,
          linkedin_link,
          specialisation,
          favourite_food,
          regions_operate_on,
          top_hobby,
          favourite_tv_show_movie,
          favourite_music,
          location,
          tags,
          profile_picture,
          other_pictures,
        }) => ({
          id,
          title: name,
          email: email,
          role: role,
          telephone: phone,
          linkedIn: linkedin_link,
          specialism: specialisation,
          food: favourite_food,
          region: regions_operate_on,
          hobby: top_hobby,
          movie: favourite_tv_show_movie,
          music: favourite_music,
          location: location.join(", "),
          tag: tags,
          profile_picture: profile_picture,
          other_pictures: other_pictures,
        }),
      );
    }
  }, [data, activeLocation]);

  const getContactDetails = (selectedLocation = null) => {
    if (!selectedLocation) {
      return null;
    }

    const contactDetail = contact.find((loc) =>
      loc.city.toLowerCase().includes(selectedLocation.toLowerCase()),
    );

    return contactDetail ? contactDetail : null;
  };

  const handleOpen = (id) => {
    setOpen(true);
    setActiveContent(id);
  };

  const handleClose = () => {
    setOpen(false);
    setActiveContent("");
  };

  const handleChange = (key) => {
    setActiveLocation(key);
    setSelectedLocationDetail(getContactDetails(key));
  };

  useEffect(() => {
    setMobile(md);
  }, [md]);

  return (
    <div className="pt-[50px] lg:pt-[86px]">
      <Modal key={Math.random()} open={open} setOpen={setOpen}>
        <ModalTeamMember
          activeContent={activeContent}
          getActiveMembers={activeMembers}
          handleClose={handleClose}
        />
      </Modal>
      <div className="flex justify-center flex-col gap-[18px]">
        <div className="flex justify-center gap-5 flex-wrap max-w-[760px] mx-auto">
          <PrimaryBtn
            bgColor={activeLocation === null ? "pink" : "grey"}
            color=""
            className="pt-[21px] pb-[18px] px-5 lg:px-7 text-base sm:text-xl/[25px] font-normal hover:bg-pink text-purple hover:text-purple"
            onClick={() => handleChange(null)}
          >
            <CustomSourceflowText
              path="location.options.all"
              locale={locale}
              title={locale === "de" ? "Alle Orte" : "All locations"}
            />
          </PrimaryBtn>
          {location.map((key) => (
            <PrimaryBtn
              bgColor={activeLocation === key ? "pink" : "grey"}
              color=""
              className="pt-[21px] pb-[18px] px-5 lg:px-7 text-base sm:text-xl/[25px] font-normal hover:bg-pink text-purple hover:text-purple"
              key={key}
              onClick={() => handleChange(key)}
            >
              {key}
            </PrimaryBtn>
          ))}
        </div>
      </div>
      <div className="flex flex-row justify-center">
        <div className="flex flex-row flex-wrap justify-center py-10 sm:pt-[74px] sm:pb-[78px] max-w-full sm:max-w-[calc(3*428px)] gap-x-[14px] gap-y-[70px]">
          {activeLocation !== null && (
            <ActiveLocationCta
              activeLocation={activeLocation}
              selectedLocationDetail={selectedLocationDetail}
              hoverColor={hoverColor}
            />
          )}
          <>
            {isMobile ? (
              <CustomSwiper
                view={{ desktop: 1, laptop: 1, md: 1, mobile: 1 }}
                space={14}
                containerStyles="max-w-full"
                navigate={true}
              >
                {activeMembers?.map(({ profile_picture, title, role, id }) => (
                  <CustomSwiperSlide key={id}>
                    <TeamMembersCard
                      profile_picture={profile_picture}
                      title={title}
                      role={role}
                      color={color}
                      hoverColor={hoverColor}
                      onClick={() => handleOpen(id)}
                    />
                  </CustomSwiperSlide>
                ))}
              </CustomSwiper>
            ) : (
              <>
                {activeMembers?.map(({ profile_picture, title, role, id }) => (
                  <TeamMembersCard
                    key={id}
                    profile_picture={profile_picture}
                    title={title}
                    role={role}
                    color={color}
                    hoverColor={hoverColor}
                    onClick={() => handleOpen(id)}
                    className="group/item"
                  />
                ))}
              </>
            )}
          </>
        </div>
      </div>
    </div>
  );
}
